import { IDesignerState } from '@/../typings';
import { filestore, FsVideo } from '@/filestore';
import { IVideoReactProps, IVideo, ITuple3 } from '@r3f-component-data-structure';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FrontSide, DoubleSide } from 'three';
import SelectedEntityPositionHotspot from '../Hotspots/SelectedEntityPositionHotspot';
import LineBox from '../LineBox/LineBox';
import CustomBillboard from '../r3f-components/components/CustomBillboard/CustomBillboard';
import EntityBoundaries from './EntityBoundaries/EntityBoundaries';
import {
	useGetHocBorderProperties,
	useGetHocCurvedProperties,
	useGetHocSpatialProperties,
	useHocEntityPointerMove,
	useHocPointerDown,
	useHocPointerMove,
	useHocPointerOut,
	useHocPointerOver,
	useHocPointerUp,
    useShowHighlight,
} from './hocCustomHooks';

interface IVideoWrapped {
	id: string;
	enabled?: boolean;
}

const withVideoBehaviour = (WrappedVideo: FunctionComponent<IVideoReactProps>): FunctionComponent<IVideoWrapped> => {
	const FuncComp: FunctionComponent<IVideoWrapped> = ({ enabled: entityIsEnabled = true, id }) => {
		// Selectors
		const entity = useSelector((state: IDesignerState) => state.contentReducer.contentDoc.componentsById[id] as IVideo);
		const projectShadowsEnabled = useSelector((state: IDesignerState) => state.contentReducer.contentDoc.shadowsEnabled ?? true);

		// Refs
		const pointerStageRef = useRef<'down' | 'move' | 'up' | null>(null);
		const shiftLockedRef = useRef<number | null>(null);
		const initialPosRef = useRef<ITuple3 | null>(null); // Used to calculate pointer offset in pointerMove
		const previousPositionRef = useRef<ITuple3 | null>(null); // The previous position, used to compare with the current position
		const pointerUpHasRunRef = useRef<boolean | null>(null); // To prevent multiple dispatched actions when pointerUp runs twice this is reset onPointerMove
		const [showEntityPositionHotspot, setShowEntityPositionHotspot] = useState(false);
		const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>();
		const [videoHasError, setVideoHasError] = useState(false);
		const [isHovered, setIsHovered] = useState(false);

		// Entity properties to pass to wrapped component
		const { opacity, hasAlpha, autoplay, chromaKey, castShadow = false, receiveShadow = false, billboard, depthTest = true, renderOrder } = entity;
		const { scale, position, rotation } = useGetHocSpatialProperties(entity);
		const { borderWidth, borderRadius, borderRgba } = useGetHocBorderProperties(entity);
		const { curvature } = useGetHocCurvedProperties(entity);

		// Pointer events
		const onPointerDown = useHocPointerDown({ pointerStageRef, initialPosRef, entity, setShowEntityPositionHotspot });
		const onPointerMove = useHocPointerMove({ pointerStageRef, initialPosRef, pointerUpHasRunRef, previousPositionRef, entity, shiftLockedRef });
		const onPointerUp = useHocPointerUp({ pointerStageRef, pointerUpHasRunRef, initialPosRef, entity, setShowEntityPositionHotspot, shiftLockedRef });
		const onPointerOver = useHocPointerOver(entity, () => setIsHovered(true));
		const onPointerOut = useHocPointerOut(entity, () => setIsHovered(false));
        const showHighlight = useShowHighlight(id, isHovered);

        const onEntityPointerMove = useHocEntityPointerMove(
			entity,
			() => setIsHovered(false),
			() => setIsHovered(true)
		);

		const key = JSON.stringify(chromaKey);

		useEffect(() => {
			try {
				const fsFile = filestore.load<FsVideo>(entity.filestoreId);
				// setVideoUrl(fsFile.getPlaylistUrl())
				setThumbnailUrl(fsFile.getThumbnailUrl());
			} catch (error) {
				setVideoHasError(true);
			}
		}, [entity.filestoreId]);

		return (
			<>
				<EntityBoundaries
					forceError={videoHasError}
					forceLoading={typeof thumbnailUrl === 'undefined'}
					position={position}
					rotation={rotation}
					scale={scale}
					renderOrder={renderOrder}
					onPointerDown={entityIsEnabled ? onPointerDown : undefined}
					onPointerUp={onPointerUp}
					label={`Video component ${id} render order ${renderOrder}`}
				>
					<>
						<WrappedVideo
							id={id}
							key={key}
							chromaKey={chromaKey}
							scale={scale}
							rotation={rotation}
							position={position}
							videoUrl={''}
							thumbnailUrl={thumbnailUrl!}
							thumbnailOnly={true}
							curvature={curvature}
							hideControls={autoplay}
							renderOrder={renderOrder}
							opacity={opacity}
							hasAlpha={hasAlpha}
							borderWidth={borderWidth}
							borderRgba={borderRgba}
							borderRadius={borderRadius}
                            depthTest={depthTest}
                            side={entity.isSnappedToTarget ? FrontSide :  DoubleSide}
							castShadow={castShadow && projectShadowsEnabled}
							receiveShadow={receiveShadow && projectShadowsEnabled}
							onPointerDown={entityIsEnabled ? onPointerDown : undefined}
							onPointerUp={entityIsEnabled ? onPointerUp : undefined}
							onPointerMove={entityIsEnabled ? onEntityPointerMove : undefined}
							onPointerOut={entityIsEnabled ? onPointerOut : undefined}
							onPointerOver={entityIsEnabled ? onPointerOver : undefined}
                            billboard={billboard}
                            isCameraOrbiting
                            rotateNoBillboardOuterGroup
						/>
                         <CustomBillboard 
                            position={position}
                            rotation={rotation}
                            follow={!!billboard?.[0]}
                            lockY={!billboard?.[1]}
                            visible={showHighlight}
                            isCameraOrbiting
                            rotateNoBillboardOuterGroup
                        >
                            <LineBox renderOrder={renderOrder} rotation={[0, 0 , 0]} scale={[scale[0] / 2, scale[1] / 2, scale[2] / 2]} color={[66, 135, 245, 1]} lineWidth={6} />
                        </CustomBillboard>
					</>
				</EntityBoundaries>
				{showEntityPositionHotspot && <SelectedEntityPositionHotspot onPointerMove={showEntityPositionHotspot ? onPointerMove : undefined} onPointerUp={entityIsEnabled ? onPointerUp : undefined} />}
			</>
		);
	};
	return FuncComp;
};

export default withVideoBehaviour;
